import { PATTERNS } from "@components/utils/constants";
import { IRequestQuoteData } from "@hooks/useRequestQuote";
import { AppUtilsContext, IAppUtilsContext } from "@providers/AppUtilsProvider";
import { Context, useContext, useEffect } from "react";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import RaQToast from "../RaQToast";
import BackButton from '@components/icons/BackButton'
import ExitButton from '@components/icons/ExitButton'

const MAX_LENGTH = 64;
const MAX_LENGTH_EMAIL = 320;
const regexNameFilter = /[^a-zA-Z .,‘’'-]/gi;
const regexPhoneFilter = /[^a-zA-Z0-9 .:;,"“”'‘’()!?+/-]/gi;
const regexEmailFilter = /[^a-zA-Z0-9 !#$%&'*+-/=?^_`{|}~.@]/gi;

interface IContactDetailsProps {
  onRequestOpen: ((isOpen: boolean) => void) & (() => void);
  onClick: (isOpen: boolean, isSuccessMobile?: boolean | undefined) => void;
  onRequestSubmit: (data: IRequestQuoteData) => Promise<void>;
  onBackClick: (isOpen: boolean) => void;
}

function ContactDetails({
                          onRequestOpen,
                          onBackClick,
                          onClick,
                          onRequestSubmit,
                        }: IContactDetailsProps): JSX.Element {
  const { setShowLoginModal, setShowHeaderMenuMobile } =
    useContext<IAppUtilsContext>(AppUtilsContext as Context<IAppUtilsContext>);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm();

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (type === "change") {
        //  CODE SNIPPET BELOW is custom js length validation as default one is not working on android devices
        let allowedLength = MAX_LENGTH;
        if (name === "email") {
          allowedLength = MAX_LENGTH_EMAIL;
        }

        // @ts-expect-error
        if (value[name].length > allowedLength) {
          // @ts-expect-error
          value[name] = value[name].slice(0, allowedLength);
          // @ts-expect-error
          setValue(name, value[name]);
        }
        // Below is not else case because if pasted it has to be validated as well

        if (name === "fullName") {
          // The other apostrophies are the ones support by iPhone Devices
          const cleanedValue = value.fullName.replaceAll(regexNameFilter, "");
          setValue("fullName", cleanedValue);
        } else if (name === "email") {
          const cleanedValue = value.email.replaceAll(regexEmailFilter, "");
          setValue("email", cleanedValue);
        } else if (name === "phoneNumber") {
          const cleanedValue = value.phoneNumber.replaceAll(
            regexPhoneFilter,
            ""
          );
          setValue("phoneNumber", cleanedValue);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    // @ts-expect-error
    const fullName = document?.getElementById("fullName")?.value.length;
    // @ts-expect-error
    const email = document?.getElementById("email")?.value.length;
    // @ts-expect-error
    const phoneNumber = document?.getElementById("phoneNumber")?.value.length;
    if (
      Object.keys(errors).length === 0 &&
      fullName &&
      email &&
      phoneNumber > 0
    )
      document
        ?.getElementById("submit-btn")
        ?.addEventListener("click", () => onClick(true, true));
  }, [errors.fullName, errors.email, errors.phoneNumber]);

  return (
    <div className="fixed left-0 top-0 right-0 z-[100] flex h-full w-full flex-col justify-between overflow-auto bg-white">
      <div>
        <form
          noValidate
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={handleSubmit(onRequestSubmit as SubmitHandler<FieldValues>)}
          className="absolute top-0 z-[60] h-full w-full bg-white scrollbar-hide"
        >
          <div className="mx-auto w-[90%]">
            <div className="mt-3 flex items-center justify-between">
              <div>
                <BackButton onClick={() => onBackClick(true)}/>
              </div>
              <div className="font-medium">Request a Custom Quote</div>
              <div>
                <ExitButton onClick={onRequestOpen}/>
              </div>
            </div>
            <div className="mt-3 mb-3 flex items-center justify-center">
              <div className="text-p14 text-midgray">Step 3 of 3</div>
              <div className="mx-3 inline-block h-4 w-[0.2px] bg-blue-20"></div>
              <div className="text-p14 text-midgray">Contact Details</div>
            </div>
            <div className="input-label">Full Name*</div>
            <input
              type="text"
              id="fullName"
              placeholder="Enter name"
              className={`input-field ${errors.fullName ? "error-field" : ""}`}
              maxLength={MAX_LENGTH}
              {...register("fullName", {
                required: {
                  value: true,
                  message: "Full Name field is required",
                },
              })}
            />
            {typeof errors.fullName?.message === 'string' && (
              <span className="error-text">{errors.fullName.message}</span>
            )}
            <div className="input-label">Email Address*</div>
            <input
              type="email"
              id="email"
              placeholder="Enter email"
              className={`input-field ${errors.fullName ? "error-field" : ""}`}
              maxLength={MAX_LENGTH_EMAIL}
              {...register("email", {
                required: { value: true, message: "Email field is required" },
                pattern: {
                  value: PATTERNS.EMAIL_VALIDATION_PATTERN,
                  message: "Please enter valid email address",
                },
              })}
            />
            {typeof errors.email?.message === 'string' && (
              <span className="error-text">{errors.email.message}</span>
            )}
            <div className="input-label">Phone</div>
            <input
              type="tel"
              inputMode="tel"
              id="phoneNumber"
              placeholder="Phone"
              className={`input-field`}
              maxLength={MAX_LENGTH}
              {...register("phoneNumber", { required: false })}
            />

            <div className="h-[280px]"></div>
            {/* {errors.phoneNumber && (
              <span className="mt-1.5 px-[25px] text-p13 text-negative-100">
                Phone Number field is required
              </span>
            )} */}
          </div>
          <RaQToast/>
          <div className="fixed bottom-0 z-10 w-full rounded-t-2xl bg-white p-[10px]">
            <button
              id="submit-btn"
              type="submit"
              className="mx-auto flex w-[90%] justify-center rounded-full bg-orange p-[1.375rem] font-semibold text-white"
            >
              Request a Custom Quote
            </button>
            <div className="mt-6">
              <div className="flex justify-center">
                <div className="text-p14 font-medium">
                  Already have an account?
                </div>
                <div
                  onClick={() => {
                    setShowLoginModal(true);
                    setShowHeaderMenuMobile(true);
                    onRequestOpen();
                  }}
                  className="ml-2 mb-4 text-p14 font-semibold text-orange"
                >
                  Log in
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ContactDetails;
